import { useState } from 'react'
import { useTranslation } from 'next-i18next'

import { IfFeatureEnabled } from '@growthbook/growthbook-react'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import PromoBadge from '~/shared/components/PromoBadge'
import PartnerOfferBadge from '~/campaigns/components/PartnerOfferBadge'
import BgPattern from '~/landing/components/BgPattern'
import IntroHero from '~/landing/components/IntroHero'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ROUTES from '~/shared/constants/routes.const'
import { GET_STARTED_ENTRY_ROUTE } from '~/get-started/entryRoute.const'
import { Button } from '~/components/Button'
import { Link } from '~/components/Link'
import StartAnalytics from '~/analytics/start/startAnalytics'
import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import MakeAWishIntroHero from '~/campaigns/make-a-wish/MakeAWishIntroHero'
import MakeAWishRegistrationForm from '~/campaigns/make-a-wish/MakeAWishRegistrationForm'
import WithRecaptchaProvider from '~/auth/components/WithRecaptchaProvider'
import { RegisterProvider } from '~/auth/contexts/RegisterContext'
import ArrowDownBoldIcon from '~/components/Icons/svgs/ArrowDownBoldIcon'

export default function IntroSection({
  onArrowClick,
  isMAWCampaign = false,
  partnerDetails,
  entryRoute = GET_STARTED_ENTRY_ROUTE,
  onCtaClick,
}: {
  onArrowClick: () => void
  isMAWCampaign?: boolean
  /** Co-brands the hero with a partner logo. */
  partnerDetails?: PartnerDetailsType
  /** Where the sign-up CTAs go. */
  entryRoute?: string
  /** Runs on CTA click; return true to stay on the page. Decided at click time, so the CTA is always a link. */
  onCtaClick?: () => boolean
}): React.ReactElement {
  const { t } = useTranslation(['start', 'register', 'subscription', 'landing'])

  // Safari/WebKit does not support interactive HTML elements (inputs, buttons, focus)
  // inside SVG <foreignObject>. On Safari, we overlay the registration form as regular
  // HTML outside the SVG so users can type and interact with it normally. This is a temporary fix
  const [isSafari] = useState(() => {
    if (typeof window === 'undefined') return false
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  })

  // Button's onClick signature; the event is React's synthetic one, so preventDefault stops the Link.
  const handleButtonClick = (e?: Event): void => {
    StartAnalytics.logCtaClick('onboarding_cta', 'landing_section_1_cta')
    if (onCtaClick?.()) e?.preventDefault()
  }
  // A partner with its own price must not advertise the global promo, unless it keeps the promo itself.
  const showPromoBadge = !isMAWCampaign && (!partnerDetails || !!partnerDetails.keepsBackToSchool2026)
  const heroNoteKey = partnerDetails?.landingHeroNoteKey

  return (
    <div className="relative">
      <div className="relative w-full h-full overflow-hidden">
        <div className="relative mx-auto w-200 h-1">
          <ResponsiveImage
            className="absolute hidden md:block min-h-[1259px] min-w-[1625px] h-full object-contain overflow-hidden z-75 top-0 left-96"
            height={1259}
            imageClassName="object-top object-contain w-full max-w-250 lg:max-w-339 h-full"
            imageWidths="1625"
            srcKey="landing/heroImageV2.png"
            width={1625}
          />
        </div>
        <BgPattern />
        <div className="flex relative w-full h-full md:h-112 lg:h-144 xl:h-178 max-w-screen-2xl text-white mx-auto overflow-hidden 2xl:overflow-visible">
          <div className="relative w-full h-full">
            {showPromoBadge && (
              <PromoBadge
                className="absolute left-1/2 top-1/2 z-100 -translate-x-1/2 translate-y-10 md:left-auto md:right-12 md:top-28 md:translate-x-20 md:translate-y-12 lg:right-24 xl:right-40"
                keyPrefix="intro.promoBadge"
                namespace="landing"
                partnerDetails={partnerDetails}
                theme="purple"
              />
            )}
            {!showPromoBadge && partnerDetails?.showOfferBadge && (
              <PartnerOfferBadge
                className="absolute left-1/2 top-1/2 z-100 -translate-x-1/2 translate-y-6 scale-75 md:left-auto md:right-12 md:top-28 md:translate-x-0 md:translate-y-12 lg:right-24 lg:translate-x-20 lg:scale-100 xl:right-40"
                partnerDetails={partnerDetails}
              />
            )}
            {isMAWCampaign ? (
              <MakeAWishIntroHero
                className="flex w-full h-full px-4 md:px-18 xl:px-28 2xl:px-36 mt-0 md:-mt-4 pointer-events-none"
                isSafari={isSafari}
              />
            ) : (
              <IntroHero
                className="flex w-full h-full px-4 md:px-18 xl:px-28 2xl:px-36 mt-0 md:-mt-4 pointer-events-none"
                partnerDetails={partnerDetails}
              />
            )}
            {/* Safari fallback: render form outside SVG since foreignObject doesn't support input interaction in WebKit */}
            {isSafari && isMAWCampaign && (
              <WithRecaptchaProvider>
                <RegisterProvider>
                  <div className="absolute top-30 md:top-2 left-5 md:left-0 md:w-3/5 md:h-full flex items-center justify-center">
                    <MakeAWishRegistrationForm />
                  </div>
                </RegisterProvider>
              </WithRecaptchaProvider>
            )}
            {!isMAWCampaign && (
              <div
                className={`absolute flex gap-4 py-2 bottom-4 left-0 mb-10 md:scale-50 lg:scale-75 xl:scale-100 ${
                  heroNoteKey ? 'md:mb-20 lg:mb-28' : 'md:mb-16 lg:mb-24'
                } xl:mb-40 md:origin-left md:ml-21 xl:ml-30 2xl:ml-40 px-8 z-50 flex-wrap -mt-6 items-start justify-center w-full max-w-[414px] md:pt-12 md:justify-start md:max-w-none md:flex-nowrap`}
              >
                <div className="flex flex-col items-center w-full md:w-auto">
                  <Button
                    className="!bg-green-lime !text-black border-4 rounded-full text-md font-black !mt-0 z-10 will-change-transform hover:scale-110 duration-300 !px-4 md:px-0 w-full md:w-auto"
                    onClick={handleButtonClick}
                    to={entryRoute}
                  >
                    {t('landing:intro.getStartedCTA')}
                  </Button>
                  <IfFeatureEnabled feature="landing-page-free-trial-text">
                    <p className="text-sm font-bold mt-2 whitespace-nowrap">{t('landing:intro.freeTrialDays')}</p>
                  </IfFeatureEnabled>
                </div>
                <div className="hidden md:flex h-12 border-r-2 my-auto" />
                <Link
                  className="will-change-transform underline text-md hover:scale-110 duration-300 my-auto"
                  to={ROUTES.LOGIN}
                >
                  {t('landing:logIn')}
                </Link>
                {heroNoteKey && (
                  // Hangs below the row, so the CTAs sit where they do on every other partner.
                  <p className="absolute top-full left-8 hidden md:block whitespace-nowrap text-5 leading-7 font-medium">
                    {t(`campaign:${heroNoteKey}`)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <button
          className="relative z-100 text-9 md:text-16 font-black leading-tight text-blue-pastel flex flex-col gap-2 items-center justify-center mx-auto -mt-14 sm:-mt-8 md:-mt-16 xl:-mt-20 mb-6 py-6 px-2 md:hover:scale-110 duration-300"
          onClick={onArrowClick}
        >
          <span className="border-3 border-green-lime rounded-full p-1">
            <SVGIcon icon={ArrowDownBoldIcon} pathClassName="text-green-lime" size={20} />
          </span>
        </button>
      </div>
    </div>
  )
}
